import { useEffect, useState } from 'react';

// `uploadEf` é tipado como `Promise<boolean>` de propósito: o hook depende do `false` para detectar falha,
// já que a implementação não lança. Um retorno mais largo deixaria uma regressão futura passar em silêncio.
export function useEfUploadStatus(params: { currentEfVersion: number | undefined; uploadEf: (file: File) => Promise<boolean>; errorMessage: string }) {
    const { currentEfVersion, uploadEf, errorMessage } = params;
    const [uploading, setUploading] = useState(false);
    const [versionBeforeUpload, setVersionBeforeUpload] = useState<number | null>(null);
    const [successVersion, setSuccessVersion] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (uploading && currentEfVersion !== undefined && currentEfVersion !== versionBeforeUpload) {
            setSuccessVersion(currentEfVersion);
            setUploading(false);
        }
    }, [uploading, currentEfVersion, versionBeforeUpload]);

    const upload = async (file: File) => {
        setSuccessVersion(null);
        setError(null);
        // `currentEfVersion` é `undefined` enquanto o desenvolvimento ainda não tem EF: o 0 representa
        // "nenhuma versão anterior" e faz o primeiro upload (que chega na versão 1) ser detectado como sucesso.
        setVersionBeforeUpload(currentEfVersion ?? 0);
        setUploading(true);
        try {
            // `uploadEf` não lança em falha de rede/API: sinaliza o erro retornando `false`. Sem tratar esse
            // retorno o `uploading` ficaria preso em `true` e o usuário não receberia nenhum feedback.
            const ok = await uploadEf(file);
            if (!ok) {
                setUploading(false);
                setError(errorMessage);
            }
        } catch {
            setUploading(false);
            setError(errorMessage);
        }
    };

    const clearSuccess = () => setSuccessVersion(null);

    const clearError = () => setError(null);

    return { successVersion, upload, clearSuccess, error, clearError };
}
